import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { SearchResult } from 'jbc-front/components/SearchForm';
import { yup } from '@/libs/yup';
import { SortingFields } from '@/components/SearchForm/SortingFields';
import { useDisplayEmployeeLimit } from '@/components/utils/display_employee_limit';
import { useDebouncedSubmit } from '@/hooks/useDebouncedSubmit';
import styles from './SearchForm.scss';
import { useSession } from '@/components';
import { hasPermission } from '@/utils/permission.ts';
import { AddUser } from '../AddUser';

const IGNORE_AUTO_SUBMIT_FIELDS = ['status', 'limit', 'sortType'];

const limits = ['10', '20', '50', '100'].map(ele => ({ value: ele, label: `${ele}件を表示` }));

const sortTypes = [
  { value: 'staff_code__asc', label: 'スタッフコード 昇順' },
  { value: 'staff_code__desc', label: 'スタッフコード 降順' },
  { value: 'name__asc', label: '氏名 昇順' },
  { value: 'name__desc', label: '氏名 降順' },
  { value: 'email__asc', label: 'メールアドレス 昇順' },
  { value: 'email__desc', label: 'メールアドレス 降順' }
];

const mainFieldSchema = yup.object({});

const additionalFieldSchema = yup.object({});

const sortTypeSchema = yup.object({
  sortType: yup
    .string()
    .oneOf(sortTypes.map(item => item.value))
    .required()
});

const paginatorSchema = yup.object({
  page: yup.string(),
  limit: yup.string()
});

const schema = mainFieldSchema.concat(additionalFieldSchema).concat(sortTypeSchema).concat(paginatorSchema);

export type Schema = yup.InferType<typeof schema>;

interface SearchFormProps {
  values: Schema;
  totalCount?: number;
  isLoading: boolean;
  onSubmit: (data: Schema) => void;
}

export const SearchForm: FC<SearchFormProps> = ({ values, totalCount, isLoading, onSubmit }) => {
  const [limit, setLimit] = useDisplayEmployeeLimit();
  const methods = useForm<Schema>({
    values,
    resolver: yupResolver(schema)
  });
  const { handleSubmit, watch, subscribe } = methods;

  const formValues = watch();
  const { page } = formValues;

  useDebouncedSubmit(subscribe, () => handleSubmit(onSubmit)(), {
    ignoreAutoSubmitFields: IGNORE_AUTO_SUBMIT_FIELDS
  });

  const { user } = useSession();
  const hasAddUserPermission =
    hasPermission(user, 'user_manage_add_user') || hasPermission(user, 'user_manage_add_lms_user');

  return (
    <FormProvider {...methods}>
      <div className={styles.searchForm}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.result}>
            <div className={styles.resultButtonContainer}>{hasAddUserPermission && <AddUser />}</div>
            <SearchResult
              className={styles.resultCenter}
              total={totalCount}
              page={page}
              limit={limit}
              isLoading={isLoading}
              failureMessage=""
            />
            <SortingFields
              limits={limits}
              onChangeLimit={newValue => {
                setLimit(newValue);
                handleSubmit(onSubmit)();
              }}
              sortTypes={sortTypes}
              onChangeSortType={() => handleSubmit(onSubmit)()}
            />
          </div>
        </form>
      </div>
    </FormProvider>
  );
};
